import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InsulatorCard } from "@/components/InsulatorCard";
import { CollectionWorldMap } from "@/components/WorldMap";
import { CollectionPassport } from "@/components/Passport";
import { Badges } from "@/components/Badges";
import { MIN_COLLECTION } from "@/lib/constants";
import { MessageSquare, ArrowLeftRight, MapPin, Heart, User, BookOpen, Globe, MessagesSquare, Lock, CalendarDays, BookMarked } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const TABS = [
  { id: "overview", label: "Overview", icon: User },
  { id: "collection", label: "Collection", icon: BookOpen },
  { id: "map", label: "World Map", icon: Globe },
  { id: "passport", label: "Passport", icon: BookMarked },
  { id: "wishlist", label: "Wishlist", icon: Heart },
  { id: "forum", label: "Forum", icon: MessagesSquare },
  { id: "trades", label: "Trades", icon: ArrowLeftRight },
];

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("overview");
  const [open, setOpen] = useState(false);
  const [offer, setOffer] = useState({ message: "", external: false });

  const [badges, setBadges] = useState([]);
  const [collection, setCollection] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [wishlist, setWishlist] = useState(null);
  const [forum, setForum] = useState(null);
  const [trades, setTrades] = useState(null);
  const [locked, setLocked] = useState(false);

  const isSelf = user && user.id === userId;

  useEffect(() => {
    setProfile(null);
    api.get(`/users/${userId}`).then(({ data }) => setProfile(data)).catch(() => setProfile(false));
    api.get(`/users/${userId}/badges`).then(({ data }) => setBadges(data)).catch(() => {});
  }, [userId]);

  useEffect(() => {
    if (!profile) return;
    if (tab === "collection" && collection === null) {
      api.get(`/users/${userId}/collection`).then(({ data }) => setCollection(data)).catch((e) => { if (e.response?.status === 403) setLocked(true); setCollection([]); });
    }
    if (tab === "map" && mapData === null) {
      api.get(`/users/${userId}/map`).then(({ data }) => setMapData(data)).catch((e) => { if (e.response?.status === 403) setLocked(true); setMapData({ total_countries: 0, countries: [] }); });
    }
    if (tab === "passport" && mapData === null) {
      api.get(`/users/${userId}/map`).then(({ data }) => setMapData(data)).catch((e) => { if (e.response?.status === 403) setLocked(true); setMapData({ total_countries: 0, countries: [] }); });
    }
    if (tab === "wishlist" && wishlist === null) {
      api.get(`/wishlist/user/${userId}`).then(({ data }) => setWishlist(data)).catch(() => setWishlist([]));
    }
    if (tab === "forum" && forum === null) {
      api.get(`/users/${userId}/forum`).then(({ data }) => setForum(data)).catch(() => setForum({ topics: [], posts: [] }));
    }
    if (tab === "trades" && trades === null) {
      api.get(`/users/${userId}/trades`).then(({ data }) => setTrades(data)).catch(() => setTrades([]));
    }
  }, [tab, profile, userId]); // eslint-disable-line

  const sendTrade = async () => {
    try {
      await api.post("/trades", { to_user_id: userId, message: offer.message, external: offer.external });
      toast.success("Trade offer sent");
      setOpen(false);
      setOffer({ message: "", external: false });
    } catch (e) {
      toast.error("Could not send offer");
    }
  };

  if (profile === false) return <div className="max-w-3xl mx-auto px-4 py-24 text-center font-display text-2xl">Collector not found.</div>;
  if (!profile) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-muted-page animate-pulse font-tech">Loading…</div>;

  const isPrivate = profile.collection_public === false && !isSelf;
  const joined = profile.created_at ? new Date(profile.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long" }) : null;
  const isCollection = profile.collection_count >= MIN_COLLECTION;

  const PrivateNote = () => (
    <div className="text-center py-16 surface border rounded-2xl" data-testid="private-collection-note">
      <Lock className="w-10 h-10 mx-auto text-muted-page mb-3" />
      <p className="font-display text-2xl">This collection is private</p>
      <p className="text-muted-page mt-1">{profile.username} has chosen to keep their collection hidden.</p>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-10">
      <div className="surface border rounded-2xl p-6 flex flex-wrap items-center gap-5" data-testid="user-profile">
        <div className="w-20 h-20 rounded-full overflow-hidden grid place-items-center text-2xl font-bold shrink-0" style={{ background: "var(--honey-500)", color: "#3a2a10" }}>
          {profile.avatar_url ? <img src={mediaUrl(profile.avatar_url)} alt="" className="w-full h-full object-cover" /> : (profile.username || "?")[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-3xl font-bold">{profile.username}</h1>
          {profile.name && profile.name !== profile.username && <p className="text-muted-page">{profile.name}</p>}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-page mt-1">
            {profile.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {profile.location}</span>}
            {joined && <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> Joined {joined}</span>}
          </div>
          {profile.bio && <p className="mt-2">{profile.bio}</p>}
          <p className="mt-2 font-tech text-sm brand-text" data-testid="collection-count-line">
            {isCollection ? `${profile.collection_count} pieces in collection` : `${profile.collection_count}/${MIN_COLLECTION} pieces — needs ${MIN_COLLECTION} to be a collection`}
          </p>
        </div>
        {user && !isSelf && (
          <div className="flex flex-col gap-2">
            <button data-testid="message-user-btn" onClick={() => navigate(`/messages/${userId}`)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white" style={{ background: "var(--brand)" }}><MessageSquare className="w-4 h-4" /> Message</button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="send-trade-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold surface border"><ArrowLeftRight className="w-4 h-4" /> Trade offer</button>
              </DialogTrigger>
              <DialogContent className="surface">
                <DialogHeader><DialogTitle className="font-display text-2xl">Send a trade offer to {profile.username}</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <textarea data-testid="trade-message-input" value={offer.message} onChange={(e) => setOffer((o) => ({ ...o, message: e.target.value }))} rows={4} placeholder="Describe your offer…" className="w-full surface border rounded-lg px-3 py-2.5" />
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" data-testid="trade-external-checkbox" checked={offer.external} onChange={(e) => setOffer((o) => ({ ...o, external: e.target.checked }))} />
                    This is an external (off-platform) trade — a surcharge applies
                  </label>
                </div>
                <DialogFooter><button data-testid="submit-trade-btn" onClick={sendTrade} className="px-5 py-2.5 rounded-full font-semibold w-full text-white" style={{ background: "var(--brand)" }}>Send offer</button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mt-8 mb-6 overflow-x-auto pb-1" data-testid="profile-tabs">
        {TABS.filter((t) => !isPrivate || !["collection", "map", "passport", "wishlist"].includes(t.id)).map((t) => (
          <button
            key={t.id}
            data-testid={`profile-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap inline-flex items-center gap-2 transition-colors ${tab === t.id ? "text-white" : "surface border"}`}
            style={tab === t.id ? { background: "var(--brand)" } : {}}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "overview" && (
        <div className="space-y-8" data-testid="tab-content-overview">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="surface border rounded-2xl p-5"><p className="text-xs font-tech uppercase tracking-widest text-muted-page">Pieces</p><p className="font-display text-3xl font-bold mt-1">{profile.collection_count}</p></div>
            <div className="surface border rounded-2xl p-5"><p className="text-xs font-tech uppercase tracking-widest text-muted-page">Awards</p><p className="font-display text-3xl font-bold mt-1 brand-text">{badges.filter((b) => b.earned).length}</p></div>
            <div className="surface border rounded-2xl p-5"><p className="text-xs font-tech uppercase tracking-widest text-muted-page">Status</p><p className="font-display text-lg font-bold mt-2">{isCollection ? "Collector" : "Getting started"}</p></div>
            <div className="surface border rounded-2xl p-5"><p className="text-xs font-tech uppercase tracking-widest text-muted-page">Visibility</p><p className="font-display text-lg font-bold mt-2">{profile.collection_public === false ? "Private" : "Public"}</p></div>
          </div>
          <Badges badges={badges} />
        </div>
      )}

      {tab === "collection" && (
        <div data-testid="tab-content-collection">
          {isPrivate || locked ? <PrivateNote /> : collection === null ? (
            <p className="text-muted-page animate-pulse font-tech">Loading…</p>
          ) : collection.length === 0 ? (
            <p className="text-muted-page">No pieces in this collection yet.</p>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {collection.filter((c) => c.insulator).map((c) => (
                <div key={c.id} className="relative">
                  <InsulatorCard ins={c.insulator} />
                  <span className="absolute top-2 right-2 text-[10px] font-tech font-bold px-2 py-1 rounded-full text-white" style={{ background: "var(--brand)" }}>{c.grade} ×{c.quantity}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "map" && (
        <div data-testid="tab-content-map">
          {isPrivate || locked ? <PrivateNote /> : mapData === null ? (
            <p className="text-muted-page animate-pulse font-tech">Loading map…</p>
          ) : (
            <CollectionWorldMap data={mapData} />
          )}
        </div>
      )}

      {tab === "passport" && (
        <div data-testid="tab-content-passport">
          {isPrivate || locked ? <PrivateNote /> : mapData === null ? (
            <p className="text-muted-page animate-pulse font-tech">Loading passport…</p>
          ) : (
            <CollectionPassport data={mapData} ownerName={profile.username} />
          )}
        </div>
      )}

      {tab === "wishlist" && (
        <div data-testid="tab-content-wishlist">
          {wishlist === null ? (
            <p className="text-muted-page animate-pulse font-tech">Loading…</p>
          ) : wishlist.filter((w) => w.insulator).length === 0 ? (
            <p className="text-muted-page">No wishlist items yet.</p>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {wishlist.filter((w) => w.insulator).map((w) => <InsulatorCard key={w.id} ins={w.insulator} />)}
            </div>
          )}
        </div>
      )}

      {tab === "forum" && (
        <div className="space-y-6" data-testid="tab-content-forum">
          {forum === null ? (
            <p className="text-muted-page animate-pulse font-tech">Loading…</p>
          ) : forum.topics.length === 0 && forum.posts.length === 0 ? (
            <p className="text-muted-page">No forum activity yet.</p>
          ) : (
            <>
              {forum.topics.length > 0 && (
                <div>
                  <h3 className="font-display text-xl font-bold mb-3">Topics started</h3>
                  <div className="grid gap-2">
                    {forum.topics.map((t) => (
                      <Link key={t.id} to={`/forum/${t.id}`} className="surface border rounded-xl px-4 py-3 flex items-center justify-between hover:shadow">
                        <span className="font-medium truncate">{t.title}</span>
                        <span className="text-xs text-muted-page shrink-0 ml-3">{t.reply_count} replies</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {forum.posts.length > 0 && (
                <div>
                  <h3 className="font-display text-xl font-bold mb-3">Recent replies</h3>
                  <div className="grid gap-2">
                    {forum.posts.slice(0, 20).map((p) => (
                      <Link key={p.id} to={`/forum/${p.topic_id}`} className="surface border rounded-xl px-4 py-3 hover:shadow block">
                        <p className="text-sm line-clamp-2">{p.body}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "trades" && (
        <div data-testid="tab-content-trades">
          {trades === null ? (
            <p className="text-muted-page animate-pulse font-tech">Loading…</p>
          ) : trades.length === 0 ? (
            <p className="text-muted-page">No trade activity yet.</p>
          ) : (
            <div className="grid gap-2">
              {trades.map((t) => (
                <div key={t.id} className="surface border rounded-xl px-4 py-3 flex items-center justify-between gap-3" data-testid="trade-row">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{t.from_name} → {t.to_name}{t.insulator_name ? ` · ${t.insulator_name}` : ""}</p>
                    <p className="text-xs text-muted-page">{t.external ? "External trade" : "On-platform"} · {t.created_at ? new Date(t.created_at).toLocaleDateString() : ""}</p>
                  </div>
                  <span className={`text-xs font-tech uppercase tracking-wider px-2 py-1 rounded-full border shrink-0 ${t.status === "accepted" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : t.status === "declined" ? "bg-rose-100 text-rose-700 border-rose-300" : "bg-amber-100 text-amber-800 border-amber-300"}`}>{t.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
